import React, { createContext, useCallback, useContext, useRef } from 'react';
import getUserId from '~/widget/utils/getUserId';

const FeatureFlags = {
  'widget-trigger---biedwijzer': [
    'control',
    'test-smart-bid',
    'test-winning-bid',
    'test-bidding-too-much',
    'test-what-to-bid',
  ],
  'widget---rotating-highlighted-app': ['control', 'rotation'],
} as const;

type FeatureFlag = keyof typeof FeatureFlags;

// Extract boolean feature flags (string values)
type BooleanFeatureFlag = {
  [K in FeatureFlag]: (typeof FeatureFlags)[K] extends string ? K : never;
}[FeatureFlag];

// Extract variant feature flags (array values)
type VariantFeatureFlag = {
  [K in FeatureFlag]: (typeof FeatureFlags)[K] extends ReadonlyArray<string>
    ? K
    : never;
}[FeatureFlag];

// Get variant tokens for a variant feature flag
type VariantTokens<T extends VariantFeatureFlag> =
  (typeof FeatureFlags)[T] extends ReadonlyArray<infer U> ? U : never;

type PosthogContextValue = {
  getFeatureFlag: {
    <T extends BooleanFeatureFlag>(featureFlag: T): boolean | null;
    <T extends VariantFeatureFlag>(featureFlag: T): VariantTokens<T> | null;
  };
  getAllFeatureFlags: () => Partial<
    Record<FeatureFlag, boolean | string | null>
  >;
};

const PosthogContext = createContext<PosthogContextValue | null>(null);

type Props = {
  children: React.ReactNode;
};

// Deterministic hash-based feature flag evaluation
const hashStringToInt = (value: string): number => {
  let hash = 0;

  for (let i = 0; i < value.length; i += 1) {
    const charCode = value.charCodeAt(i);
    hash = (hash << 5) - hash + charCode;
    hash |= 0; // Convert to 32bit integer
  }

  // Ensure non-negative integer
  return Math.abs(hash);
};

/**
 * Deterministically returns a boolean for a given feature flag and user id.
 * Same (featureFlag, userId) pair will always return the same value.
 */
export const getDeterministicFeatureFlagValue = (
  featureFlag: string,
  userId: string,
): boolean => {
  const seed = `${featureFlag}:${userId}`;
  const hash = hashStringToInt(seed);

  // Simple 50/50 split based on parity
  return hash % 2 === 0;
};

/**
 * Deterministically returns a variant token from an array for a given feature flag and user id.
 * Same (featureFlag, userId) pair will always return the same variant.
 */
const getDeterministicVariantValue = (
  featureFlag: string,
  variants: ReadonlyArray<string>,
  userId: string,
): string => {
  const seed = `${featureFlag}:${userId}`;
  const hash = hashStringToInt(seed);

  // Use modulo to select a variant deterministically
  const index = hash % variants.length;
  return variants[index]!;
};

const PosthogContextProvider: React.FC<Props> = ({ children }) => {
  // Use ref so updating latestByType does NOT trigger rerenders

  // Cache for feature flag results - only stores flags that have been requested
  const featureFlagCacheRef = useRef<
    Partial<Record<FeatureFlag, boolean | string | null>>
  >({});

  const getFeatureFlag = useCallback(
    <T extends FeatureFlag>(featureFlag: T) => {
      // Check cache first
      const cachedValue = featureFlagCacheRef.current[featureFlag];
      if (cachedValue !== undefined) {
        return cachedValue as T extends BooleanFeatureFlag
          ? boolean | null
          : T extends VariantFeatureFlag
            ? VariantTokens<T> | null
            : never;
      }

      const flagConfig = FeatureFlags[featureFlag];
      const userId = getUserId();
      let result: boolean | string;

      // Check if it's a variant flag (array) or boolean flag (string)
      if (Array.isArray(flagConfig)) {
        result = getDeterministicVariantValue(featureFlag, flagConfig, userId);
      } else {
        result = getDeterministicFeatureFlagValue(featureFlag, userId);
      }

      // Store in cache
      featureFlagCacheRef.current[featureFlag] = result;

      return result as T extends BooleanFeatureFlag
        ? boolean
        : T extends VariantFeatureFlag
          ? VariantTokens<T>
          : never;
    },
    [],
  ) as PosthogContextValue['getFeatureFlag'];

  const getAllFeatureFlags = useCallback(
    (): Partial<Record<FeatureFlag, boolean | string>> =>
      // Only return cached feature flags (flags that have been requested)
      // Filter out null values
      Object.entries(featureFlagCacheRef.current).reduce(
        (acc, [key, value]) => {
          if (value === null) return acc;

          acc[key as FeatureFlag] = value as boolean | string;
          return acc;
        },
        {} as Partial<Record<FeatureFlag, boolean | string>>,
      ),
    [],
  );

  const value: PosthogContextValue = {
    getFeatureFlag,
    getAllFeatureFlags,
  };

  return (
    <PosthogContext.Provider value={value}>{children}</PosthogContext.Provider>
  );
};

export const usePosthog = () => {
  const ctx = useContext(PosthogContext);

  if (!ctx) {
    throw new Error('usePosthog must be used within an PosthogContextProvider');
  }

  return ctx;
};

export default PosthogContextProvider;
